import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Loader, LogIn } from 'lucide-react';
import api from '../api/api';
import useAuthStore from '../store/authStore';
import Logo from '../components/Logo';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  // FORGOT PASSWORD POPUP STATES
  const [showForgot, setShowForgot] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [otp, setOTP] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [popupLoading, setPopupLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/auth/login', { email, password });
      setAuth(res.data.user, res.data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 🟠 SEND OTP
  const requestReset = async () => {
    try {
      setPopupLoading(true);
      await api.post('/auth/request-reset', { email });
      setInfoMsg('OTP sent to your email.');
      setShowForgot(false);
      setShowOTP(true);
    } catch (err) {
      setInfoMsg(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setPopupLoading(false);
    }
  };

  // 🔵 VERIFY OTP
  const verifyOTP = async () => {
    try {
      setPopupLoading(true);
      await api.post('/auth/verify-otp', { email, otp });
      setInfoMsg('OTP verified!');
      setShowOTP(false);
      setShowReset(true);
    } catch (err) {
      setInfoMsg(err.response?.data?.message || 'Invalid OTP.');
    } finally {
      setPopupLoading(false);
    }
  };

  // 🟣 RESET PASSWORD
  const resetPassword = async () => {
    try {
      setPopupLoading(true);
      await api.post('/auth/reset-password', { email, otp, newPassword });
      setInfoMsg('Password reset successful! You can now log in.');
      setShowReset(false);
    } catch (err) {
      setInfoMsg(err.response?.data?.message || 'Error resetting password.');
    } finally {
      setPopupLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center bg-gray-50 dark:bg-slate-900 py-4 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-xl rounded-2xl p-8">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-md mb-4">
            <Logo />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Welcome Back
          </h1>
        </div>

        {infoMsg && (
          <p className="text-center text-blue-600 dark:text-blue-400 mb-4 text-sm">{infoMsg}</p>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white border border-gray-300 dark:border-slate-600 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-sm text-orange-600 hover:text-orange-700 mt-2"
            >
              Forgot password?
            </button>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg btn-primary hover:bg-[#14bceb] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Sign In
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="mt-6 text-center text-gray-600 dark:text-gray-400 text-sm">
          Don't have an account?{' '}
          <Link to="/register" className="text-orange-600 hover:text-orange-700 font-semibold">
            Sign up here
          </Link>
        </p>
      </div>

      {/* ------------------------------------------- */}
      {/* POPUP 1 — SEND OTP */}
      {/* ------------------------------------------- */}
      {showForgot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl w-80 shadow-lg">
            <h2 className="text-lg font-bold mb-3 text-gray-800 dark:text-white">
              Reset Password
            </h2>

            <input
              type="email"
              placeholder="Enter your email"
              className="w-full border dark:border-slate-600 rounded p-2 mb-3 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <button
              onClick={requestReset}
              className="w-full bg-orange-600 text-white py-2 rounded"
            >
              {popupLoading ? 'Sending...' : 'Send OTP'}
            </button>

            <button
              className="w-full text-gray-500 mt-3"
              onClick={() => setShowForgot(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------- */}
      {/* POPUP 2 — VERIFY OTP */}
      {/* ------------------------------------------- */}
      {showOTP && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl w-80 shadow-lg">
            <h2 className="text-lg font-bold mb-3 text-gray-800 dark:text-white">
              Enter OTP
            </h2>

            <input
              type="text"
              maxLength={6}
              placeholder="6-digit OTP"
              className="w-full border dark:border-slate-600 rounded p-2 mb-3 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
              value={otp}
              onChange={(e) => setOTP(e.target.value)}
            />

            <button
              onClick={verifyOTP}
              className="w-full bg-green-600 text-white py-2 rounded"
            >
              {popupLoading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button
              className="w-full text-gray-500 mt-3"
              onClick={() => setShowOTP(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------- */}
      {/* POPUP 3 — RESET PASSWORD */}
      {/* ------------------------------------------- */}
      {showReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl w-80 shadow-lg">
            <h2 className="text-lg font-bold mb-3 text-gray-800 dark:text-white">
              Set New Password
            </h2>

            <input
              type="password"
              placeholder="New password"
              className="w-full border dark:border-slate-600 rounded p-2 mb-3 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <button
              onClick={resetPassword}
              className="w-full bg-purple-600 text-white py-2 rounded"
            >
              {popupLoading ? 'Saving...' : 'Reset Password'}
            </button>

            <button
              className="w-full text-gray-500 mt-3"
              onClick={() => setShowReset(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
